/* ==========================================================================
   FUEL BUILD A BOX controller.

   RJ, 2026-09-01 (1): "Bundling feature is missing - need that mechanism in -
   leverage Zeigarnik effect UX."
   RJ, 2026-09-01 (2): "the bundle feature must show the % offer and add on top
   of it as the user keeps adding, build this logic and add micro interactions
   and motion animations that are gratifying."

   THE FOUR THINGS THAT MAKE THIS ZEIGARNIK AND NOT JUST A PROGRESS BAR - see
   the long note in sections/fuel-box.liquid - are all in here:
     1. renderTray() draws EMPTY slots before anything is chosen.
     2. ctaLabel() names the number still missing, on the button.
     3. save()/load() persist the half-built box across sessions.
     4. paint() marks completion, so the tension gets released.

   THE LADDER, ADDED 2026-09-01 FOR RJ'S SECOND INSTRUCTION
   A tier is `minimum quantity : percent off : Shopify discount code`, parsed
   off data-tiers. Three rules govern it and none of them are negotiable:

     A TIER WITH NO CODE IS NOT A TIER. It is dropped at parse time and never
     rendered. A percentage on the page that no Shopify discount will honour
     is a number the checkout disagrees with, which is the one failure mode
     this section is not allowed to have. Empty tiers setting = the section
     renders exactly as it did before the ladder existed.

     THE DISCOUNT IS APPLIED BY SHOPIFY, NOT BY US. We never send a price. We
     POST the line items, then hand off to /discount/<CODE>?redirect=/cart,
     which is Shopify's own code-application route. Shopify re-checks the
     minimum quantity server side, so a tampered client gets nothing. The
     percentage on screen is a PREVIEW of a discount the platform owns.

     ROUNDING FOLLOWS AN ORDER-LEVEL PERCENTAGE. saving() is
     round(subtotal x pct), which is what Shopify computes for an order-level
     percentage discount. If the codes are ever rebuilt as PRODUCT-level
     discounts the platform rounds per line instead and our preview can drift
     a rupee. The codes must be order-level. See APPLY.md.

   "ADD ON TOP OF IT AS THE USER KEEPS ADDING" is read as an ESCALATING LADDER,
   not a compounding one. Discounts do not stack on themselves - 8% then 15%
   is 15%, never 23% - because Shopify applies one code and compounding a
   percentage on a percentage is a margin decision no interface should be
   making on its own. What escalates is the RUNG: the next one is always named
   and always reachable, so closing one loop opens the next.

   No framework, no build step, no dependency. State is one plain object.

   THE UNIT GUARD, TASK-297 (2026-09-02). RJ's ladder is BOXES, not line
   items: a Protein Top-up or a Functional shake picked from this same grid
   must never fill a slot, never count toward a rung, and never be able to
   drive the CTA into its enabled/redirect state on its own. `catalog[id].isUnit`
   comes off `data-fuel-box-unit`, which sections/fuel-box.liquid sets from
   `product_type contains 'Bowl'` - see that file for why this is a
   structural check and not a handle list. Everywhere this file used to read
   `chosen()`/`gross()` as "every picked item" it now reads "every picked
   BOX"; a top-up still steps, still rides in the same /cart/add.js POST, and
   still totals into what the customer pays, it just cannot itself complete
   the Stack or reach a discount rung. See ADD-ONS in the totals panel for
   where its price actually shows.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCE = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  function reduced() { return !!REDUCE.matches; }

  /* ----------------------------------------------------------------------
     Money. The shop's own money_format comes in on a data attribute, because
     it is a SETTING and cannot be read from JavaScript. Reimplementing the
     four placeholders is the standard Shopify helper; inventing a currency
     string here would print a number the checkout then disagrees with.
     ---------------------------------------------------------------------- */
  function group(number, thousands, decimal, places) {
    var value = (number / 100).toFixed(places);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
    return places > 0 && parts[1] ? whole + decimal + parts[1] : whole;
  }

  function money(cents, format) {
    var fmt = format || '{{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount_no_decimals': return group(cents, ',', '.', 0);
        case 'amount_with_comma_separator': return group(cents, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator': return group(cents, '.', ',', 0);
        default: return group(cents, ',', '.', 2);
      }
    });
  }

  /* ----------------------------------------------------------------------
     MOTION HELPERS

     Everything below is decoration on a mechanism that already works. Each
     one returns early under prefers-reduced-motion and each one is written so
     that if the animation never runs, or is interrupted halfway, the DOM is
     still in its finished state. A half-played animation must never be able
     to leave a price wrong or a slot blank.
     ---------------------------------------------------------------------- */

  var easeOut = function (t) { return 1 - Math.pow(1 - t, 3); };

  /* THE BACKSTOP THAT MAKES A ROLLING NUMBER SAFE TO SHIP.

     requestAnimationFrame DOES NOT FIRE IN A BACKGROUND TAB. A tween started
     on the tap that filled the box, on a customer who then switched tabs, gets
     no frames at all - it freezes on whatever intermediate number the last
     frame drew and stays there. Caught live on theme 157917773962,
     2026-09-01: a box whose real total was Rs. 1,484.88 sat reading
     Rs. 1,399.77, and per-bowl sat at Rs. 249.00 on its way to Rs. 247.48.
     Those are wrong prices displayed with total confidence.

     A rolling number is decoration. A wrong price is not. So every tween now
     carries a setTimeout that lands the final value - timers still fire in a
     hidden tab, throttled but alive - and `document.hidden` short-circuits the
     animation entirely when we already know there will be no frames. The
     element reaches its true value within the tween's own duration whether or
     not a single frame ever renders.

     Both helpers below share the pattern; tween() is the shared engine and
     takes the formatter, so the guarantee cannot be implemented correctly in
     one and forgotten in the other. */
  function tween(el, from, to, ms, render) {
    if (!el) return;
    if (el._raf) { cancelAnimationFrame(el._raf); el._raf = 0; }
    if (el._land) { clearTimeout(el._land); el._land = 0; }

    if (reduced() || from === to || document.hidden) {
      el.textContent = render(to);
      return;
    }

    var span = ms;
    var start = 0;
    function frame(now) {
      if (!start) start = now;
      var t = Math.min(1, (now - start) / span);
      el.textContent = render(Math.round(from + (to - from) * easeOut(t)));
      if (t < 1) { el._raf = requestAnimationFrame(frame); } else { el._raf = 0; }
    }
    el._raf = requestAnimationFrame(frame);

    el._land = setTimeout(function () {
      if (el._raf) { cancelAnimationFrame(el._raf); el._raf = 0; }
      el._land = 0;
      el.textContent = render(to);
    }, span + 120);
  }

  /* Rolling money. The value is tweened in CENTS and re-rendered through
     money() every frame, so the separators and the currency symbol stay the
     shop's own rather than being reconstructed here. Tabular figures in the
     CSS stop the column from twitching while the digits move. */
  function tweenMoney(el, from, to, format, ms) {
    tween(el, from, to, ms || 420, function (v) { return money(v, format); });
  }

  /* The same tween for a bare integer, used by the percentage badge. */
  function tweenInt(el, from, to, ms) {
    tween(el, from, to, ms || 480, function (v) { return String(v); });
  }

  /* Restart a CSS animation on an element that may still be mid-animation.
     Removing the class and reading offsetWidth forces the reflow that lets
     the same animation play twice in a row - without it, tapping + on two
     bowls quickly animates the first and silently drops the second. */
  function replay(el, cls) {
    if (!el || reduced()) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  }

  /* THE FLY. A ghost of the bowl photograph arcs from the card the customer
     tapped into the slot it is about to occupy. This is the one animation
     that is doing real work rather than decorating: the picker and the tray
     are two separate regions of the page and, on desktop, the tray is in the
     opposite column. Without the arc, a tap over here changes something over
     there and the eye has to go looking for it. The arc is the causal link.

     Built on getBoundingClientRect + WAAPI on a position:fixed clone, so it
     never reflows the grid it is flying out of. */
  function fly(fromEl, toEl, src, done) {
    if (reduced() || !fromEl || !toEl || !src || !window.Element ||
        typeof fromEl.animate !== 'function') { if (done) done(); return; }

    var a = fromEl.getBoundingClientRect();
    var b = toEl.getBoundingClientRect();
    if (!a.width || !b.width) { if (done) done(); return; }

    var ghost = document.createElement('div');
    ghost.className = 'fuel-box__ghost';
    ghost.style.left = a.left + 'px';
    ghost.style.top = a.top + 'px';
    ghost.style.width = a.width + 'px';
    ghost.style.height = a.height + 'px';
    var img = document.createElement('img');
    img.src = src;
    img.alt = '';
    ghost.appendChild(img);
    document.body.appendChild(ghost);

    var dx = b.left - a.left;
    var dy = b.top - a.top;
    var sx = b.width / a.width;
    var sy = b.height / a.height;

    /* The lift on the middle keyframe is what makes it read as thrown rather
       than dragged. Scaled to the distance so a short hop on a phone does not
       loop over the header. */
    var lift = Math.min(70, Math.max(18, Math.abs(dy) * 0.35 + 18));

    var anim = ghost.animate([
      { transform: 'translate(0px, 0px) scale(1, 1)', opacity: 1 },
      { transform: 'translate(' + (dx * 0.5) + 'px, ' + (dy * 0.5 - lift) + 'px) ' +
                   'scale(' + ((1 + sx) / 2) + ', ' + ((1 + sy) / 2) + ')',
        opacity: 1, offset: 0.55 },
      { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(' + sx + ', ' + sy + ')',
        opacity: 0.9 }
    ], { duration: 460, easing: 'cubic-bezier(0.34, 0.9, 0.28, 1)', fill: 'forwards' });

    var landed = false;
    function land() {
      if (landed) return;
      landed = true;
      if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      if (done) done();
    }
    anim.onfinish = land;
    /* WAAPI onfinish does not fire if the tab is backgrounded mid-flight, and
       a ghost left pinned over the page is worse than no animation at all. */
    setTimeout(land, 700);
  }

  /* Phones only. A 12ms tick on a tier unlock, nothing else - haptics on every
     tap would be noise, and the unlock is the only moment worth marking in a
     channel the customer is not looking at. */
  function tick(ms) {
    if (reduced()) return;
    try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) {}
  }

  function setup(root) {
    var tray = root.querySelector('[data-fuel-box-tray]');
    var fill = root.querySelector('[data-fuel-box-fill]');
    var sweep = root.querySelector('[data-fuel-box-sweep]');
    var pipWrap = root.querySelector('[data-fuel-box-pips]');
    var count = root.querySelector('[data-fuel-box-count]');
    var totalEl = root.querySelector('[data-fuel-box-total]');
    var wasEl = root.querySelector('[data-fuel-box-was]');
    var eachEl = root.querySelector('[data-fuel-box-each]');
    var saveWrap = root.querySelector('[data-fuel-box-savewrap]');
    var saveEl = root.querySelector('[data-fuel-box-save]');
    var addonsWrap = root.querySelector('[data-fuel-box-addonswrap]');
    var addonsEl = root.querySelector('[data-fuel-box-addons]');
    var deal = root.querySelector('[data-fuel-box-deal]');
    var badge = root.querySelector('[data-fuel-box-badge]');
    var pctEl = root.querySelector('[data-fuel-box-pct]');
    var dealText = root.querySelector('[data-fuel-box-dealtext]');
    var nextEl = root.querySelector('[data-fuel-box-next]');
    var cta = root.querySelector('[data-fuel-box-add]');
    var ctaLabelEl = root.querySelector('[data-fuel-box-ctalabel]');
    var cards = Array.prototype.slice.call(
      root.querySelectorAll('[data-fuel-box-product]')
    );
    if (!tray || !cta || cards.length === 0) return;

    var FORMAT = root.getAttribute('data-money-format') || '{{amount}}';
    var KEY = root.getAttribute('data-box-key') || 'fuel-box';

    /* ------------------------------------------------------------------
       THE LADDER. "6:8:FUELBOX8, 12:15:FUELBOX15" becomes a sorted list of
       {min, pct, code}. A row missing any of the three is dropped, silently
       and on purpose: the theme editor is a text field and a typo there must
       degrade to NO CLAIM, never to a claim the checkout will refuse.
       ------------------------------------------------------------------ */
    function parseTiers(raw) {
      if (!raw) return [];
      return raw.split(',').map(function (chunk) {
        var bits = chunk.split(':');
        var min = parseInt((bits[0] || '').trim(), 10);
        var pct = parseFloat((bits[1] || '').trim());
        var code = (bits[2] || '').trim();
        if (!min || min < 1) return null;
        if (!pct || pct <= 0 || pct >= 100) return null;
        if (!code) return null;          /* no code, no tier, no claim */
        return { min: min, pct: pct, code: code };
      }).filter(Boolean).sort(function (x, y) { return x.min - y.min; });
    }

    var TIERS = parseTiers(root.getAttribute('data-tiers'));
    var SIZES = Array.prototype.slice.call(root.querySelectorAll('[data-fuel-box-size]'))
      .map(function (b) { return parseInt(b.getAttribute('data-fuel-box-size'), 10); });

    function tierFor(n) {
      var found = null;
      for (var i = 0; i < TIERS.length; i++) if (n >= TIERS[i].min) found = TIERS[i];
      return found;
    }

    function nextTier(n) {
      for (var i = 0; i < TIERS.length; i++) if (n < TIERS[i].min) return TIERS[i];
      return null;
    }

    var catalog = {};
    cards.forEach(function (card) {
      catalog[card.getAttribute('data-id')] = {
        id: card.getAttribute('data-id'),
        title: card.getAttribute('data-title'),
        price: parseInt(card.getAttribute('data-price'), 10) || 0,
        image: card.getAttribute('data-image') || '',
        /* TASK-297. FAILS CLOSED: only an explicit "true" counts as a Box.
           A markup change that drops or mistypes this attribute makes that
           card behave like a top-up - it still steps and still adds to
           cart, it just stops filling a slot - which is a loud, visible bug
           (a box that will not complete) rather than the silent one this
           whole guard exists to prevent (a discount that quietly re-earns
           itself on cross-sell items). Same direction guard.py already
           takes on theme writes: fail closed, never fail open. */
        isUnit: card.getAttribute('data-fuel-box-unit') === 'true',
        card: card
      };
    });

    var wasFull = false;
    var lastTierMin = -1;      /* which rung we were on at the last paint */
    var lastNet = 0;           /* for the rolling totals */
    var lastEach = 0;
    var lastSave = 0;
    var lastPct = 0;
    var primed = false;        /* false until the first paint has been drawn */

    var state = {
      size: parseInt(root.getAttribute('data-default-size'), 10) || 6,
      grain: '',
      items: {}
    };

    /* ------------------------------------------------------------------
       Persistence. Wrapped, because localStorage throws outright in a
       private window on some browsers and a half-built box is not worth
       taking the section down for.
       ------------------------------------------------------------------ */
    function save() {
      try {
        localStorage.setItem(KEY, JSON.stringify(state));
      } catch (e) { /* quota, private mode, or blocked. Not fatal. */ }
    }

    function load() {
      var raw;
      try {
        raw = localStorage.getItem(KEY);
      } catch (e) { return; }
      if (!raw) return;
      try {
        var saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return;
        if (saved.size) state.size = saved.size;
        if (saved.grain) state.grain = saved.grain;
        // Only ids that are still on sale today. A box saved last week can
        // name a bowl that has since sold out, and restoring it would put a
        // slot in the tray that the customer cannot check out.
        if (saved.items) {
          Object.keys(saved.items).forEach(function (id) {
            if (catalog[id]) state.items[id] = saved.items[id];
          });
        }
      } catch (e) { /* corrupt blob. Start clean rather than throw. */ }
    }

    /* TASK-297. Every count and total the ladder reasons about now comes in
       a UNIT (real Box) and an ADD-ON pair. chosen()/unitGross() are what
       the tier, the tray, the pips and the CTA read - a top-up quantity
       never reaches them. addonsChosen()/addonGross() exist only so the
       panel can be honest that those items are in the Stack total without
       ever letting them earn the discount that total implies. */
    function chosen() {
      var n = 0;
      Object.keys(state.items).forEach(function (id) {
        var meta = catalog[id];
        if (meta && meta.isUnit) n += state.items[id];
      });
      return n;
    }

    function addonsChosen() {
      var n = 0;
      Object.keys(state.items).forEach(function (id) {
        var meta = catalog[id];
        if (meta && !meta.isUnit) n += state.items[id];
      });
      return n;
    }

    function unitGross() {
      var t = 0;
      Object.keys(state.items).forEach(function (id) {
        var meta = catalog[id];
        if (meta && meta.isUnit) t += meta.price * state.items[id];
      });
      return t;
    }

    function addonGross() {
      var t = 0;
      Object.keys(state.items).forEach(function (id) {
        var meta = catalog[id];
        if (meta && !meta.isUnit) t += meta.price * state.items[id];
      });
      return t;
    }

    /* Order-level percentage rounding. See the header note: the Shopify codes
       must be order-level for this single round() to be the same arithmetic
       the checkout does. */
    function saving(subtotal, tier) {
      if (!tier) return 0;
      return Math.round(subtotal * tier.pct / 100);
    }

    /* ------------------------------------------------------------------
       THE OPEN LOOP, RESTATED ON THE BUTTON. This is Zeigarnik point 2, now
       carrying the rung as well as the count - the button is the last thing
       read before the tap, so the earned percentage belongs on it.
       ------------------------------------------------------------------ */
    function ctaLabel(n, tier) {
      var left = state.size - n;
      if (n === 0) return 'Pick ' + state.size + ' boxes';
      if (left > 0) return 'Pick ' + left + ' more box' + (left === 1 ? '' : 'es');
      /* A comma, not a dash. The house voice bans em dashes outright, and a
         middle dot is a non-ASCII byte that theme_push.py refuses because it
         breaks the upload. Both roads out of a fancy separator are closed, so
         the label is a plain sentence fragment and reads better for it. */
      if (tier) return 'Add ' + state.size + ' boxes, ' + tier.pct + '% off';
      return 'Add ' + state.size + ' boxes to cart';
    }

    /* ------------------------------------------------------------------
       THE RUNGS, DRAWN ON THE BAR. A pip sits at the position its tier
       occupies in the CURRENT box, so the customer can see the next rung
       coming before they reach it rather than being told about it after.
       A tier beyond the current box size (15% at 12 bowls, while a 6 box is
       open) is drawn hard against the end and labelled as the upgrade.
       ------------------------------------------------------------------ */
    function renderPips(n) {
      if (!pipWrap) return;
      pipWrap.textContent = '';
      TIERS.forEach(function (t) {
        var pip = document.createElement('span');
        pip.className = 'fuel-box__pip';
        var beyond = t.min > state.size;
        var pos = beyond ? 100 : (t.min / state.size) * 100;
        /* Clamped off both ends. The bar clips its overflow, so a rung that
           lands exactly at the box size sits at 100% and has half its width
           cut away - the marker for the LAST and best rung is the one that
           renders worst. 98.5% keeps it whole and still reads as the end. */
        pip.style.left = Math.max(1.5, Math.min(98.5, pos)) + '%';
        if (beyond) pip.classList.add('is-beyond');
        if (n >= t.min) pip.classList.add('is-won');
        pip.setAttribute('data-pip-min', String(t.min));
        pipWrap.appendChild(pip);
      });
    }

    /* ------------------------------------------------------------------
       THE SLOTS. Empty ones are drawn FIRST and always. This is Zeigarnik
       point 1: the gap is the mechanism, the counter is only a caption.

       RECONCILED, NOT REBUILT. The previous version wiped the tray with
       textContent = '' on every paint. That is fine for correctness and fatal
       for motion: every slot remounts, so every slot replays its entrance
       animation on every single tap, and there is no stable element for a
       flying ghost to aim at. Slot ELEMENTS are now created once per box
       size; only their contents change. The return value is the index of the
       slot that just became filled, which is what the fly animation targets.
       ------------------------------------------------------------------ */
    function slots() {
      return Array.prototype.slice.call(tray.children);
    }

    function ensureSlots() {
      var have = tray.children.length;
      if (have === state.size) return;
      if (have > state.size) {
        while (tray.children.length > state.size) {
          tray.removeChild(tray.lastChild);
        }
        return;
      }
      for (var i = have; i < state.size; i++) {
        var cell = document.createElement('div');
        cell.className = 'fuel-box__slot';
        cell.setAttribute('role', 'listitem');
        tray.appendChild(cell);
        /* Only stagger the slots that are genuinely new to the page, so
           switching 6 -> 12 grows six more rather than redrawing twelve. */
        if (!reduced()) {
          cell.style.animationDelay = ((i - have) * 34) + 'ms';
          cell.classList.add('is-arriving');
          (function (c) {
            setTimeout(function () {
              c.classList.remove('is-arriving');
              c.style.animationDelay = '';
            }, 600 + (i - have) * 34);
          })(cell);
        }
      }
    }

    function renderTray() {
      ensureSlots();

      /* TASK-297. An add-on never occupies a slot - it is not part of the
         box being counted, so it cannot be part of the box being drawn. */
      var filled = [];
      Object.keys(state.items).forEach(function (id) {
        var meta = catalog[id];
        if (!meta || !meta.isUnit) return;
        for (var i = 0; i < state.items[id]; i++) filled.push(meta);
      });

      var newlyFilled = -1;
      slots().forEach(function (cell, index) {
        var item = filled[index] || null;
        var had = cell.getAttribute('data-slot-id') || '';
        var want = item ? item.id : '';
        if (had === want) return;                 /* untouched, leave it alone */

        if (want && !had) newlyFilled = index;
        cell.setAttribute('data-slot-id', want);
        cell.textContent = '';

        if (!item) {
          cell.classList.remove('is-filled');
          cell.setAttribute('aria-label', 'Empty slot ' + (index + 1));
          replay(cell, 'is-emptied');
          return;
        }

        cell.classList.add('is-filled');
        cell.removeAttribute('aria-label');

        if (item.image) {
          var img = document.createElement('img');
          img.src = item.image;
          img.alt = '';
          img.setAttribute('aria-hidden', 'true');
          cell.appendChild(img);
        } else {
          // Several Fuel bowls still have no photograph (TASK-252). A filled
          // slot that is simply blank looks like a rendering failure, so name
          // the bowl instead. Never a stock image and never a drawn
          // placeholder dressed as a photograph.
          var label = document.createElement('span');
          label.className = 'fuel-box__slotname';
          label.textContent = item.title;
          cell.appendChild(label);
        }
        var drop = document.createElement('button');
        drop.type = 'button';
        drop.className = 'fuel-box__drop';
        drop.setAttribute('data-drop', item.id);
        drop.setAttribute('aria-label', 'Take ' + item.title + ' out of the box');
        drop.textContent = 'x';
        cell.appendChild(drop);

        replay(cell, 'is-landed');
      });

      return newlyFilled;
    }

    /* ------------------------------------------------------------------
       THE DEAL BLOCK. Nothing renders here at all when TIERS is empty, which
       is the state the section ships in until the Shopify codes exist.
       ------------------------------------------------------------------ */
    function renderDeal(n, tier) {
      if (!deal) return;
      if (TIERS.length === 0) { deal.hidden = true; return; }
      deal.hidden = false;

      var next = nextTier(n);
      root.classList.toggle('has-deal', !!tier);

      if (tier) {
        tweenInt(pctEl, lastPct, tier.pct);
        if (dealText) {
          dealText.textContent = tier.pct + '% off your Stack, applied at checkout.';
        }
      } else {
        /* No rung earned yet. The badge previews the FIRST rung rather than
           showing a zero, because a 0% badge reads as "there is no offer"
           when the truth is "the offer is three bowls away". lastPct is reset
           so the count-up on the real unlock always runs from zero. */
        if (pctEl) pctEl.textContent = next ? String(next.pct) : '0';
        lastPct = 0;
        if (dealText) {
          dealText.textContent = next
            ? 'Your discount starts at ' + next.min + ' boxes.'
            : '';
        }
      }

      if (!nextEl) return;
      nextEl.textContent = '';
      nextEl.classList.remove('is-upgrade');

      if (!next) {
        if (tier) {
          nextEl.textContent = 'Best price in the range. Nothing above this.';
        }
        return;
      }

      var gap = next.min - n;

      /* The rung is past the end of the current box. This is the AOV moment
         and it is the only place in the section that offers to change a
         setting on the customer's behalf, so it is a real button with the
         consequence written on it - never an auto-switch. */
      if (next.min > state.size && SIZES.indexOf(next.min) !== -1) {
        var up = document.createElement('button');
        up.type = 'button';
        up.className = 'fuel-box__upgrade';
        up.setAttribute('data-fuel-box-upgrade', String(next.min));
        up.textContent = 'Go to a ' + next.min + '-box Stack for ' + next.pct + '% off';
        nextEl.appendChild(up);
        nextEl.classList.add('is-upgrade');
        return;
      }

      nextEl.textContent = gap === 1
        ? 'One more box unlocks ' + next.pct + '% off.'
        : gap + ' more boxes unlock ' + next.pct + '% off.';
    }

    /* ------------------------------------------------------------------
       THE UNLOCK. Zeigarnik's own finding is that the tension releases on
       completion; a rung that lands with no acknowledgement has spent the
       customer's attention and paid nothing back. So the moment a tier is
       crossed: the badge pops, the pip rings, a sweep runs the length of the
       filled bar, and a phone gets a single haptic tick. Once, on the
       transition - never on a repaint that happens to still be above the
       line, or holding + becomes a strobe.
       ------------------------------------------------------------------ */
    function celebrate(tier) {
      replay(badge, 'is-won');
      replay(sweep, 'is-sweeping');
      replay(deal, 'is-flash');
      if (pipWrap) {
        var pip = pipWrap.querySelector('[data-pip-min="' + tier.min + '"]');
        replay(pip, 'is-ringing');
      }
      tick(12);
    }

    function paint(source) {
      var n = chosen();
      var full = n >= state.size;
      var tier = tierFor(n);

      var newlyFilled = renderTray();
      renderPips(n);

      /* The ghost flies from the card that was tapped into the slot that just
         became filled. Both ends have to exist - a keyboard user stepping the
         quantity, or a restore from localStorage, has no source card, and
         gets the plain landing animation instead. */
      if (source && newlyFilled > -1) {
        var target = tray.children[newlyFilled];
        var shot = source.querySelector('.fuel-box__shot');
        var item = catalog[source.getAttribute('data-id')];
        if (target && shot && item && item.image) {
          target.classList.add('is-landing');
          fly(shot, target, item.image, function () {
            target.classList.remove('is-landing');
            replay(target, 'is-landed');
          });
        }
      }

      if (fill) fill.style.width = Math.min(100, (n / state.size) * 100) + '%';
      if (count) {
        count.textContent = full
          ? 'Box full. ' + state.size + ' of ' + state.size + ' chosen.'
          : n + ' of ' + state.size + ' chosen';
      }

      /* TASK-297. `sub` is the BOX subtotal only - the ladder's saving() is
         computed against that, never against an add-on's price, because a
         top-up was never priced to carry the Stack discount. `addons` rides
         into the visible total (the customer is really charged for it) but
         never through saving(), so the struck-through "was" price and the
         "you save" figure both stay true to what Shopify will actually
         apply at checkout. */
      var sub = unitGross();
      var off = saving(sub, tier);
      var addons = addonGross();
      var addonQty = addonsChosen();
      var net = sub - off + addons;
      var each = n ? Math.round((sub - off) / n) : 0;

      tweenMoney(totalEl, lastNet, net, FORMAT);
      tweenMoney(saveEl, lastSave, off, FORMAT);
      if (eachEl) {
        if (n) { tweenMoney(eachEl, lastEach, each, FORMAT); }
        else { eachEl.textContent = '--'; }
      }
      /* The struck original only appears when there is a real discount behind
         it. A strike-through with nothing taken off it is the oldest lie in
         Indian D2C and this section does not tell it. */
      if (wasEl) {
        wasEl.hidden = off === 0;
        if (off) wasEl.textContent = money(sub + addons, FORMAT);
      }
      if (saveWrap) saveWrap.hidden = off === 0;
      if (addonsWrap) addonsWrap.hidden = addonQty === 0;
      if (addonsEl && addonQty > 0) {
        addonsEl.textContent = money(addons, FORMAT) + ' (' + addonQty +
          (addonQty === 1 ? ' item, not part of the Stack' : ' items, not part of the Stack') + ')';
      }

      lastNet = net;
      lastEach = each;
      lastSave = off;

      renderDeal(n, tier);
      if (tier) lastPct = tier.pct;

      cta.disabled = !full;
      var label = ctaLabel(n, tier);
      if (ctaLabelEl) {
        if (ctaLabelEl.textContent !== label) {
          ctaLabelEl.textContent = label;
          replay(ctaLabelEl, 'is-swapped');
        }
      } else {
        cta.textContent = label;
      }
      root.classList.toggle('is-complete', full);   /* Zeigarnik point 4 */

      /* A rung was just crossed upward. `primed` is what keeps the very first
         paint silent: a box restored from localStorage is already sitting on
         a rung it earned yesterday, and replaying the unlock for it is a
         celebration of nothing. */
      var min = tier ? tier.min : -1;
      if (primed && tier && min > lastTierMin) celebrate(tier);
      lastTierMin = min;

      // On a phone the panel sits above the grid and does not stick, so the
      // moment the box fills the button is somewhere up the page. Bring it
      // back ONCE, on the transition into complete - not on every paint, or
      // every tap of the sixth bowl's minus button yanks the page around.
      if (full && !wasFull && window.matchMedia('(max-width: 899px)').matches) {
        var panel = root.querySelector('.fuel-box__panel');
        if (panel && panel.scrollIntoView) {
          panel.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
        }
      }
      wasFull = full;

      cards.forEach(function (card) {
        var cardId = card.getAttribute('data-id');
        var q = state.items[cardId] || 0;
        var out = card.querySelector('[data-fuel-box-qty]');
        if (out && out.textContent !== String(q)) {
          out.textContent = String(q);
          replay(out, 'is-bumped');
        }
        card.classList.toggle('is-picked', q > 0);
        var more = card.querySelector('[data-fuel-box-more]');
        /* TASK-297. The box being full only stops MORE BOXES. An add-on's +
           is never capped by the Stack size - it is not spending a slot, so
           it never runs out of one. */
        var meta = catalog[cardId];
        var isUnitCard = !!(meta && meta.isUnit);
        if (more) more.disabled = isUnitCard && full;
        var less = card.querySelector('[data-fuel-box-less]');
        if (less) less.disabled = q === 0;
      });

      /* The grid dims its unpickable cards once the box is full rather than
         only disabling the buttons, so the reason the + stopped working is
         visible instead of inferred. */
      root.classList.toggle('is-locked', full);

      save();
    }

    function bump(id, delta, source) {
      var q = state.items[id] || 0;
      var meta = catalog[id];
      var isUnitItem = !!(meta && meta.isUnit);
      /* TASK-297. The capacity check only ever applies to a real Box - an
         add-on is never what "full" means, so it is never what refuses. */
      if (delta > 0 && isUnitItem && chosen() >= state.size) {
        /* The box is full and something was still pressed. Say so with the
           panel rather than doing nothing at all. */
        replay(tray, 'is-refusing');
        return;
      }
      q += delta;
      if (q <= 0) {
        delete state.items[id];
      } else {
        state.items[id] = q;
      }
      paint(delta > 0 ? source : null);
    }

    /* ---- wiring ---------------------------------------------------- */
    cards.forEach(function (card) {
      var id = card.getAttribute('data-id');
      var more = card.querySelector('[data-fuel-box-more]');
      var less = card.querySelector('[data-fuel-box-less]');
      if (more) more.addEventListener('click', function () {
        replay(more, 'is-pressed');
        bump(id, 1, card);
      });
      if (less) less.addEventListener('click', function () { bump(id, -1, null); });
    });

    tray.addEventListener('click', function (event) {
      var drop = event.target.closest('[data-drop]');
      if (drop) bump(drop.getAttribute('data-drop'), -1, null);
    });

    /* The upgrade button is re-rendered on every paint, so it is bound by
       delegation on the panel rather than per element. */
    if (nextEl) {
      nextEl.addEventListener('click', function (event) {
        var up = event.target.closest('[data-fuel-box-upgrade]');
        if (!up) return;
        var want = parseInt(up.getAttribute('data-fuel-box-upgrade'), 10);
        var button = root.querySelector('[data-fuel-box-size="' + want + '"]');
        if (button) button.click();
      });
    }

    function setSize(next) {
      state.size = next;
      root.querySelectorAll('[data-fuel-box-size]').forEach(function (b) {
        b.setAttribute('aria-pressed',
          parseInt(b.getAttribute('data-fuel-box-size'), 10) === next ? 'true' : 'false');
      });
      // Shrinking the box must not silently drop bowls the customer chose.
      // Trim from the end of the list, newest pick first, until it fits.
      // TASK-297: trim only real Boxes. An add-on never counts toward
      // chosen(), so decrementing one here would never shrink the number
      // that is actually over the new size - it would only throw away a
      // top-up the customer picked for no reason at all.
      var ids = Object.keys(state.items).filter(function (id) {
        var meta = catalog[id];
        return meta && meta.isUnit;
      });
      while (chosen() > state.size && ids.length) {
        var last = ids[ids.length - 1];
        bumpQuiet(last, -1);
        if (!state.items[last]) ids.pop();
      }
      /* Growing the box drops the customer BELOW the rung they were on a
         moment ago. That is not a loss and must not be celebrated in reverse,
         so the rung memory is reset to the new count's own tier before the
         paint that follows. */
      var t = tierFor(chosen());
      lastTierMin = t ? t.min : -1;
      paint(null);
    }

    root.querySelectorAll('[data-fuel-box-size]').forEach(function (button) {
      button.addEventListener('click', function () {
        setSize(parseInt(button.getAttribute('data-fuel-box-size'), 10));
      });
    });

    function bumpQuiet(id, delta) {
      var q = (state.items[id] || 0) + delta;
      if (q <= 0) { delete state.items[id]; } else { state.items[id] = q; }
    }

    root.querySelectorAll('[data-fuel-box-filter]').forEach(function (button) {
      button.addEventListener('click', function () {
        var want = button.getAttribute('data-fuel-box-filter');
        root.querySelectorAll('[data-fuel-box-filter]').forEach(function (b) {
          b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
        });
        root.querySelectorAll('.fuel-box__cell').forEach(function (cell) {
          var show = want === 'all' || cell.getAttribute('data-diet') === want;
          cell.hidden = !show;
        });
      });
    });

    root.querySelectorAll('[data-fuel-box-grain]').forEach(function (input) {
      input.addEventListener('change', function () {
        state.grain = input.value;
        save();
      });
      if (input.checked && !state.grain) state.grain = input.value;
    });

    /* ---- add to cart ------------------------------------------------
       ONE request with an items array, not one request per bowl. Six
       sequential /cart/add.js calls means six cart events, six chances to
       half-fail, and a cart that is wrong in a way the customer sees.

       Then, if a rung was earned, the redirect goes through Shopify's own
       /discount/<CODE>?redirect=/cart rather than to /cart directly. That
       route is what applies the code to the session; there is no supported
       way to attach a discount from /cart/add.js, and writing the percentage
       into a line item property would print a number nothing enforces.
       ------------------------------------------------------------------ */
    cta.addEventListener('click', function () {
      if (cta.disabled) return;

      var items = Object.keys(state.items).map(function (id) {
        var line = { id: parseInt(id, 10), quantity: state.items[id] };
        if (state.grain) {
          line.properties = {
            // Same property name the PDP writes, so a box order and a single
            // bowl order read identically in fulfilment.
            Grain: state.grain === 'rice' ? 'Rice' : 'Pearl millet (bajra)'
          };
        }
        return line;
      });

      var tier = tierFor(chosen());

      cta.disabled = true;
      var was = ctaLabelEl ? ctaLabelEl.textContent : cta.textContent;
      if (ctaLabelEl) { ctaLabelEl.textContent = 'Packing the box'; }
      else { cta.textContent = 'Packing the box'; }
      cta.classList.add('is-working');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart rejected the box');
          try { localStorage.removeItem(KEY); } catch (e) {}
          window.location.href = tier
            ? '/discount/' + encodeURIComponent(tier.code) + '?redirect=/cart'
            : '/cart';
        })
        .catch(function () {
          cta.disabled = false;
          cta.classList.remove('is-working');
          if (ctaLabelEl) { ctaLabelEl.textContent = was; }
          else { cta.textContent = was; }
          var fine = root.querySelector('[data-fuel-box-fine]');
          if (fine) {
            fine.textContent =
              'The cart did not take the Stack. One of these boxes may have just sold out. Reload and try again.';
            fine.classList.add('fuel-box__fine--err');
          }
        });
    });

    load();
    // Restore the saved grain onto the control, or the radio and the state
    // disagree and the order ships the grain the customer did not pick.
    if (state.grain) {
      var savedGrain = root.querySelector('[data-fuel-box-grain][value="' + state.grain + '"]');
      if (savedGrain) savedGrain.checked = true;
    }
    root.querySelectorAll('[data-fuel-box-size]').forEach(function (b) {
      b.setAttribute('aria-pressed',
        parseInt(b.getAttribute('data-fuel-box-size'), 10) === state.size ? 'true' : 'false');
    });
    /* A restored box must not fire the unlock animation on page load - the
       rung was earned yesterday and replaying it is a celebration of nothing.
       Seed the rung memory from the restored count before the first paint. */
    var restored = tierFor(chosen());
    lastTierMin = restored ? restored.min : -1;
    lastPct = restored ? restored.pct : 0;
    paint(null);
    primed = true;
    root.classList.add('is-ready');
  }

  function init() {
    var roots = document.querySelectorAll('[data-fuel-box]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
